import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { api } from '../utils/api';
import { useAuth } from './AuthContext';

const defaultWorkspaceContextValue = {
  organizations: [],
  currentOrganization: null,
  currentTeam: null,
  teams: [],
  userTeams: [], // Teams where user is a member
  workspaceMode: 'personal', // 'personal' | 'organization' | 'team'
  loading: true,
  error: null,
  setCurrentOrganization: () => {},
  setCurrentTeam: () => {},
  setWorkspaceMode: () => {},
  refreshOrganizations: async () => {},
  refreshTeams: async () => {},
  clearWorkspace: () => {},
  isOrgAdmin: () => false,
  getUserTeamIds: () => [],
  isShapeUpEnabled: () => false,
  isScrumEnabled: () => true,
  hasStakeholderViews: () => false,
  getMethodology: () => 'scrum',
};

const WorkspaceContext = createContext(defaultWorkspaceContextValue);

export const WorkspaceProvider = ({ children }) => {
  const { isAuthenticated, loading: authLoading } = useAuth();

  const [organizations, setOrganizations] = useState([]);
  const [currentOrganization, setCurrentOrganizationState] = useState(null);
  const [currentTeam, setCurrentTeamState] = useState(null);
  const [teams, setTeams] = useState([]);
  const [userTeams, setUserTeams] = useState([]); // Teams where user is a member
  const [workspaceMode, setWorkspaceModeState] = useState('personal');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Whether the current workspace is one the person picked, rather than one
  // picked for them below. Only a real choice is written to storage, so the
  // default can be applied on every load without ever overriding someone who
  // deliberately went back to their personal workspace.
  const explicitChoice = useRef(false);

  // Load saved workspace from localStorage
  useEffect(() => {
    const savedWorkspace = localStorage.getItem('workspace');
    if (savedWorkspace) {
      try {
        const { mode } = JSON.parse(savedWorkspace);
        setWorkspaceModeState(mode || 'personal');
        // The organization and team behind it are resolved once the data loads.
      } catch (e) {
        console.error('Error parsing saved workspace:', e);
      }
    }
  }, []);

  // Fetch organizations when authenticated
  const refreshOrganizations = useCallback(async () => {
    if (!isAuthenticated) {
      setOrganizations([]);
      return;
    }

    try {
      const response = await api.get('/organizations');
      setOrganizations(response.data);
      setError(null);
      return response.data;
    } catch (err) {
      console.error('Error fetching organizations:', err);
      setError(err.response?.data?.message || 'Failed to fetch organizations');
      return [];
    }
  }, [isAuthenticated]);

  // Fetch teams for current organization
  const refreshTeams = useCallback(async (orgId) => {
    if (!orgId) {
      setTeams([]);
      setUserTeams([]);
      return [];
    }

    try {
      const response = await api.get(`/organizations/${orgId}/teams`);
      setTeams(response.data);
      // Filter to only teams where user is a member
      const memberTeams = response.data.filter(team => team.isMember);
      setUserTeams(memberTeams);
      setError(null);
      return response.data;
    } catch (err) {
      console.error('Error fetching teams:', err);
      setError(err.response?.data?.message || 'Failed to fetch teams');
      return [];
    }
  }, []);

  // Load initial data
  useEffect(() => {
    const loadInitialData = async () => {
      if (authLoading) return;

      if (!isAuthenticated) {
        setLoading(false);
        setOrganizations([]);
        setTeams([]);
        setUserTeams([]);
        setCurrentOrganizationState(null);
        setCurrentTeamState(null);
        setWorkspaceModeState('personal');
        // Signing out is not a choice to work personally, and leaving the flag
        // set would record one for whoever signs in next.
        explicitChoice.current = false;
        return;
      }

      setLoading(true);
      try {
        const orgs = await refreshOrganizations();

        // Restore saved workspace state
        let restored = false;
        const savedWorkspace = localStorage.getItem('workspace');
        if (savedWorkspace) {
          const { mode, orgId, teamId, explicit } = JSON.parse(savedWorkspace);

          if (mode === 'organization' || mode === 'team') {
            const org = orgs.find(o => o.id === orgId);
            if (org) {
              setCurrentOrganizationState(org);
              const orgTeams = await refreshTeams(org.id);

              if (mode === 'team' && teamId) {
                const team = orgTeams.find(t => t.id === teamId);
                if (team) {
                  setCurrentTeamState(team);
                  setWorkspaceModeState('team');
                } else {
                  setWorkspaceModeState('organization');
                }
              } else {
                setWorkspaceModeState('organization');
              }
              restored = true;
              explicitChoice.current = true;
            } else {
              // Org no longer exists, reset to personal
              setWorkspaceModeState('personal');
              localStorage.removeItem('workspace');
            }
          } else if (mode === 'personal' && explicit) {
            // Someone chose their personal workspace on purpose, so leave them
            // in it. Older versions wrote 'personal' without this flag simply
            // because it was the starting state, which is not a choice.
            restored = true;
            explicitChoice.current = true;
          }
        }

        // Nothing chosen and there is an organization to be in: start there.
        // A personal workspace is the exception rather than the norm, and
        // defaulting to it hides the team's work behind a menu.
        if (!restored && orgs.length > 0) {
          const org = orgs[0];
          setCurrentOrganizationState(org);
          setWorkspaceModeState('organization');
          await refreshTeams(org.id);
        }
      } catch (err) {
        console.error('Error loading initial workspace data:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    loadInitialData();
  }, [isAuthenticated, authLoading, refreshOrganizations, refreshTeams]);

  // refreshOrganizations replaces the list, but currentOrganization is a separate
  // snapshot taken when the workspace loaded. Re-point it at the fresh copy so
  // setting changes like methodology apply without a reload.
  useEffect(() => {
    if (!currentOrganization) return;
    const fresh = organizations.find(o => o.id === currentOrganization.id);
    if (fresh && fresh !== currentOrganization) {
      setCurrentOrganizationState(fresh);
    }
  }, [organizations, currentOrganization]);

  // The initial-load effect only defaults into an organization on sign-in, when a
  // brand-new user may still have none. Joining one afterwards (accepting an
  // invite) refreshes the list but leaves currentOrganization null, so the nav
  // falls back to the scrum default and shows planning poker even for a Shape Up
  // org. Adopt the first organization once one appears, unless the user has
  // deliberately chosen their personal workspace.
  useEffect(() => {
    if (loading || !isAuthenticated) return;
    if (currentOrganization || explicitChoice.current) return;
    if (organizations.length === 0) return;

    const org = organizations[0];
    setCurrentOrganizationState(org);
    setWorkspaceModeState('organization');
    refreshTeams(org.id);
  }, [loading, isAuthenticated, currentOrganization, organizations, refreshTeams]);

  // Save workspace to localStorage when it changes. An automatic selection is
  // deliberately not written: it is recomputed on each load, so it cannot
  // outlive the reason it was chosen.
  useEffect(() => {
    if (!explicitChoice.current) return;

    if (workspaceMode === 'personal') {
      localStorage.setItem('workspace', JSON.stringify({ mode: 'personal', explicit: true }));
    } else if (workspaceMode === 'organization' && currentOrganization) {
      localStorage.setItem('workspace', JSON.stringify({
        mode: 'organization',
        orgId: currentOrganization.id,
        explicit: true,
      }));
    } else if (workspaceMode === 'team' && currentTeam) {
      localStorage.setItem('workspace', JSON.stringify({
        mode: 'team',
        orgId: currentOrganization?.id,
        teamId: currentTeam.id,
        explicit: true,
      }));
    }
  }, [workspaceMode, currentOrganization, currentTeam]);

  const setCurrentOrganization = useCallback(async (org) => {
    explicitChoice.current = true;
    setCurrentOrganizationState(org);
    setCurrentTeamState(null);

    if (org) {
      setWorkspaceModeState('organization');
      await refreshTeams(org.id);
    } else {
      setWorkspaceModeState('personal');
      setTeams([]);
      setUserTeams([]);
    }
  }, [refreshTeams]);

  const setCurrentTeam = useCallback((team) => {
    explicitChoice.current = true;
    if (team) {
      setCurrentTeamState(team);
      setWorkspaceModeState('team');
    } else {
      setCurrentTeamState(null);
      if (currentOrganization) {
        setWorkspaceModeState('organization');
      } else {
        setWorkspaceModeState('personal');
      }
    }
  }, [currentOrganization]);

  const setWorkspaceMode = useCallback((mode) => {
    explicitChoice.current = true;
    setWorkspaceModeState(mode);
    if (mode === 'personal') {
      setCurrentOrganizationState(null);
      setCurrentTeamState(null);
      setTeams([]);
      setUserTeams([]);
    }
  }, []);

  const clearWorkspace = useCallback(() => {
    setCurrentOrganizationState(null);
    setCurrentTeamState(null);
    setTeams([]);
    setUserTeams([]);
    setWorkspaceModeState('personal');
    // Losing the organization you were in is not a choice to work personally, so
    // the slate is wiped and the next load picks a workspace again.
    explicitChoice.current = false;
    localStorage.removeItem('workspace');
  }, []);

  // Get workspace label for display
  const getWorkspaceLabel = useCallback(() => {
    if (workspaceMode === 'team' && currentTeam) {
      return currentTeam.name;
    }
    if (workspaceMode === 'organization' && currentOrganization) {
      return currentOrganization.name;
    }
    return 'Personal';
  }, [workspaceMode, currentOrganization, currentTeam]);

  // Check if user can create items in current workspace
  const canCreateInWorkspace = useCallback(() => {
    if (workspaceMode === 'personal') return true;
    if (workspaceMode === 'organization' && currentOrganization) {
      return ['owner', 'admin', 'member'].includes(currentOrganization.role);
    }
    if (workspaceMode === 'team' && currentTeam) {
      return currentTeam.isMember;
    }
    return false;
  }, [workspaceMode, currentOrganization, currentTeam]);

  // Check if user is admin/owner of the current organization
  const isOrgAdmin = useCallback(() => {
    if (!currentOrganization) return false;
    return ['owner', 'admin'].includes(currentOrganization.role);
  }, [currentOrganization]);

  // Get list of team IDs where user is a member
  const getUserTeamIds = useCallback(() => {
    return userTeams.map(team => team.id);
  }, [userTeams]);

  // Shape Up is opt-in per organization. Surface it whenever any organization
  // the user belongs to has turned it on, so it stays reachable no matter which
  // workspace is currently selected.
  const isShapeUpEnabled = useCallback(() => {
    return organizations.some(org => ['shape-up', 'both'].includes(org.settings?.methodology));
  }, [organizations]);

  // Whether any organization the user belongs to has at least one stakeholder
  // view configured. Lets the nav hide the Stakeholders link until an admin
  // creates the first one, matching how isShapeUpEnabled spans every org.
  const hasStakeholderViews = useCallback(() => {
    return organizations.some(org => (org.stakeholderViewCount || 0) > 0);
  }, [organizations]);

  // Methodology of the currently selected organization
  const getMethodology = useCallback(() => {
    return currentOrganization?.settings?.methodology || 'scrum';
  }, [currentOrganization]);

  // Unlike isShapeUpEnabled, this follows only the selected organization: a
  // personal workspace has no methodology, so switching to it always brings
  // planning poker back.
  const isScrumEnabled = useCallback(() => {
    return getMethodology() !== 'shape-up';
  }, [getMethodology]);

  return (
    <WorkspaceContext.Provider
      value={{
        organizations,
        currentOrganization,
        currentTeam,
        teams,
        userTeams,
        workspaceMode,
        loading,
        error,
        setCurrentOrganization,
        setCurrentTeam,
        setWorkspaceMode,
        refreshOrganizations,
        refreshTeams,
        clearWorkspace,
        getWorkspaceLabel,
        canCreateInWorkspace,
        isOrgAdmin,
        getUserTeamIds,
        isShapeUpEnabled,
        isScrumEnabled,
        hasStakeholderViews,
        getMethodology,
      }}
    >
      {children}
    </WorkspaceContext.Provider>
  );
};

export const useWorkspace = () => {
  return useContext(WorkspaceContext);
};
